import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

// ######### STYLES
const sectionRouteDetailStyle = (theme, clientColor) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "#ffffff",
  },
  // Dimension cards section
  cardsSection: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #e0e0e0",
  },
  dimensionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    justifyContent: "flex-start",
  },
  dimensionCard: {
    cursor: "pointer",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "6px",
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease-in-out",
    textAlign: "center",
    minWidth: "120px",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: clientColor,
    },
    "&.selected": {
      borderColor: clientColor,
      borderWidth: "2px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
  },
  dimensionTitle: {
    fontSize: "0.8125rem",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: theme.spacing(0.75),
  },
  dimensionValue: {
    fontSize: "1.5rem",
    fontWeight: "600",
    color: clientColor,
  },
  // Details section
  scrollableContent: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  columnsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  columnBlock: {
    flex: "1 1 auto",
    minWidth: "120px",
    maxWidth: "300px",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  columnHeader: {
    fontSize: "0.75rem",
    fontWeight: "600",
    padding: theme.spacing(1),
    borderBottom: `1px solid ${grayColor[11]}`,
    color: grayColor[2],
    textAlign: "center",
    backgroundColor: "#fafafa",
    flexShrink: 0,
  },
  columnContent: {
    flex: 1,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f9f9f9",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#ccc",
      borderRadius: "2px",
      "&:hover": {
        background: "#999",
      },
    },
  },
  columnValue: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.75, 1),
    color: grayColor[2],
    borderBottom: `1px solid ${grayColor[12]}`,
    wordBreak: "break-word",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(3),
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  emptyStateText: {
    fontSize: "0.9rem",
    color: "#999",
    textAlign: "center",
    fontStyle: "italic",
  },
  moreText: {
    padding: theme.spacing(1),
    fontStyle: "italic",
    color: grayColor[1],
    fontSize: "0.7rem",
    textAlign: "center",
    borderTop: `1px solid ${grayColor[12]}`,
    backgroundColor: "#fafafa",
  },
  viewLink: {
    color: clientColor,
    cursor: "pointer",
    fontStyle: "normal",
    fontWeight: "500",
    marginLeft: theme.spacing(0.5),
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

// ######### COMPONENT
export default function SectionRouteDetail({
  variantData,
  clientColor,
  isLoading,
  inputColumns,
}) {
  const useStyles = makeStyles((theme) =>
    sectionRouteDetailStyle(theme, clientColor),
  );
  const classes = useStyles();

  // Local state for dimension selection
  const [selectedDimension, setSelectedDimension] = React.useState(null);

  // Local state for expanded columns (tracks which columns are showing all values)
  const [expandedColumns, setExpandedColumns] = React.useState({});

  // Helper function to get column name from column_id
  const getColumnName = (columnId) => {
    if (!inputColumns || !inputColumns.model01) return columnId;
    const column = inputColumns.model01.columns?.[columnId];
    return column?.column_name || columnId;
  };

  // Reset selected dimension and expanded columns when variant changes
  React.useEffect(() => {
    setSelectedDimension(null);
    setExpandedColumns({});
  }, [variantData]);

  // Toggle column expansion
  const toggleColumnExpansion = (columnId) => {
    setExpandedColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Get selected dimension data
  const selectedDimensionData = selectedDimension
    ? variantData?.[selectedDimension]
    : null;

  // Format dimension name for display
  const formatDimensionName = (dimension) => {
    return dimension
      .replace("node1", "Node 1")
      .replace("node2", "Node 2")
      .replace("node3", "Node 3")
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Handle dimension card click
  const handleDimensionClick = (dimension) => {
    setSelectedDimension(dimension);
  };

  // Loading state
  if (isLoading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.scrollableContent}>
          <Box className={classes.emptyStateContainer}>
            <ThreeDots color={clientColor} ariaLabel="loading" />
            <Typography className={classes.emptyStateText}>
              Loading model dimensions...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // No data state
  if (!variantData) {
    return (
      <Box className={classes.container}>
        <Box className={classes.scrollableContent}>
          <Box className={classes.emptyStateContainer}>
            <Typography className={classes.emptyStateText}>
              No data found for this variant
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const hasDetails =
    selectedDimensionData?.details &&
    Object.keys(selectedDimensionData.details).length > 0;
  const columns = hasDetails ? Object.keys(selectedDimensionData.details) : [];
  const displayLimit = 10;

  return (
    <Box className={classes.container}>
      {/* Dimension Cards */}
      <Box className={classes.cardsSection}>
        <Box className={classes.dimensionsContainer}>
          {["product", "channel", "region", "node1", "node2", "node3"].map(
            (dimension) => {
              const data = variantData[dimension];
              const displayName = formatDimensionName(dimension);

              return (
                <Box
                  key={dimension}
                  className={
                    selectedDimension === dimension
                      ? `${classes.dimensionCard} selected`
                      : classes.dimensionCard
                  }
                  onClick={() => handleDimensionClick(dimension)}
                >
                  <Typography className={classes.dimensionTitle}>
                    {displayName}
                  </Typography>
                  <Typography className={classes.dimensionValue}>
                    {data?.total || 0}
                  </Typography>
                </Box>
              );
            },
          )}
        </Box>
      </Box>

      {/* Details Content */}
      <Box className={classes.scrollableContent}>
        {selectedDimension ? (
          hasDetails ? (
            <Box className={classes.columnsContainer}>
              {columns.map((columnId) => {
                const columnValues = selectedDimensionData.details[columnId];
                const isExpanded = expandedColumns[columnId];
                const valuesToShow = isExpanded
                  ? columnValues
                  : columnValues.slice(0, displayLimit);
                const hasMore = columnValues.length > displayLimit;

                return (
                  <Box key={columnId} className={classes.columnBlock}>
                    {/* Column Header */}
                    <Box className={classes.columnHeader}>
                      {getColumnName(columnId)}
                    </Box>

                    {/* Column Values */}
                    <Box
                      className={`${classes.columnContent} ${isExpanded ? "expanded" : ""}`}
                    >
                      {valuesToShow.map((value, index) => (
                        <Box key={index} className={classes.columnValue}>
                          {value}
                        </Box>
                      ))}
                    </Box>

                    {/* More text with (view) link */}
                    {hasMore && !isExpanded && (
                      <Box className={classes.moreText}>
                        ... and {columnValues.length - displayLimit} more
                        <span
                          className={classes.viewLink}
                          onClick={() => toggleColumnExpansion(columnId)}
                        >
                          (view)
                        </span>
                      </Box>
                    )}

                    {/* Collapse link when expanded */}
                    {isExpanded && (
                      <Box className={classes.moreText}>
                        <span
                          className={classes.viewLink}
                          onClick={() => toggleColumnExpansion(columnId)}
                        >
                          (collapse)
                        </span>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box className={classes.emptyStateContainer}>
              <Typography className={classes.emptyStateText}>
                No details available for this dimension
              </Typography>
            </Box>
          )
        ) : (
          <Box className={classes.emptyStateContainer}>
            <Typography className={classes.emptyStateText}>
              Click on a dimension card to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ######### PROPTYPES
SectionRouteDetail.propTypes = {
  variantData: PropTypes.object,
  clientColor: PropTypes.string,
  isLoading: PropTypes.bool,
  inputColumns: PropTypes.object,
};
